"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";

type ProviderComponent = ComponentType<{ children: ReactNode }>;
type RuntimeComponent = ComponentType;

/**
 * Keep optional telemetry out of the initial render/LCP window while still
 * starting it promptly for an engaged visitor. `requestIdleCallback` alone is
 * not a delay: on a quiet page it can fire during the first second of loading.
 */
function afterTelemetryDelayOrIntent(
  callback: () => void,
  timeoutMs = 30000,
): () => void {
  let done = false;
  const events = ["pointerdown", "keydown", "touchstart"] as const;
  const run = () => {
    if (done) return;
    done = true;
    window.clearTimeout(timeoutId);
    events.forEach((event) => window.removeEventListener(event, run));
    callback();
  };
  const timeoutId = window.setTimeout(run, timeoutMs);

  events.forEach((event) =>
    window.addEventListener(event, run, { passive: true, once: true }),
  );

  return () => {
    done = true;
    window.clearTimeout(timeoutId);
    events.forEach((event) => window.removeEventListener(event, run));
  };
}

export function ClientAnalytics({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [PostHogProvider, setPostHogProvider] =
    useState<ProviderComponent | null>(null);
  const [VercelAnalytics, setVercelAnalytics] =
    useState<RuntimeComponent | null>(null);
  const [SpeedInsights, setSpeedInsights] = useState<RuntimeComponent | null>(
    null,
  );
  const vercelAnalyticsEnabled =
    process.env.NEXT_PUBLIC_ENABLE_VERCEL_ANALYTICS?.trim() === "1" &&
    process.env.NEXT_PUBLIC_DISABLE_VERCEL_ANALYTICS?.trim() !== "1";
  const speedInsightsEnabled =
    process.env.NEXT_PUBLIC_ENABLE_SPEED_INSIGHTS?.trim() === "1";
  const postHogEnabled = pathname !== "/";
  useEffect(() => {
    if (!postHogEnabled) return;
    return afterTelemetryDelayOrIntent(() => {
      void import("@/components/providers/PostHogProvider").then((mod) => {
        setPostHogProvider(() => mod.PostHogProvider);
      });
    });
  }, [postHogEnabled]);

  useEffect(() => {
    if (!vercelAnalyticsEnabled) return;
    return afterTelemetryDelayOrIntent(() => {
      void import("@vercel/analytics/react").then((mod) => {
        setVercelAnalytics(() => mod.Analytics);
      });
    });
  }, [vercelAnalyticsEnabled]);

  useEffect(() => {
    if (!speedInsightsEnabled) return;
    return afterTelemetryDelayOrIntent(() => {
      void import("@vercel/speed-insights/next").then((mod) => {
        setSpeedInsights(() => mod.SpeedInsights);
      });
    });
  }, [speedInsightsEnabled]);

  return (
    <>
      {children}
      {/* Keep the application tree stable when telemetry finishes loading.
          Re-parenting children under the lazy provider remounts every active
          form and dialog, which can erase input on the first interaction. The
          PostHog module uses its global client, so a provider sidecar is enough
          to initialize and flush deferred events without owning page state. */}
      {PostHogProvider && <PostHogProvider>{null}</PostHogProvider>}
      {VercelAnalytics && <VercelAnalytics />}
      {SpeedInsights && <SpeedInsights />}
    </>
  );
}
